/**
 * HTML → pixels for the multi-language receipt, and the QR/barcode images its template embeds.
 *
 * Browser-only (html-to-image draws through the DOM), which is why this is a host port and the
 * package owns only the pure pixel → ESC/POS encoding. Bodies are v1's
 * (`helper/printHelper.js` `renderHtmlStringToPixelData`, `generateQrBase64`,
 * `generateBarcodeBase64`), with the width passed in pixels because the package computes it.
 *
 * ⚠️ v1 keeps its own copies until the POS converges (docs/flows/PRINTING.md §10) — change both.
 */
import * as htmlToImage from "html-to-image";
import QRCode from "qrcode";
import JsBarcode from "jsbarcode";
import type { Rasterizer } from "@rista/printing";

/** How long the offscreen node stays mounted after the draw — v1's value. */
const CLEANUP_MS = 5000;

async function toPixels(html: string, widthPx: number) {
  const node = document.createElement("div");
  node.innerHTML = html;
  Object.assign(node.style, { position: "fixed", top: "0px", left: "0px", width: `${widthPx}px`, zIndex: "-9999", visibility: "visible" });
  document.body.appendChild(node);
  try {
    const canvas = await htmlToImage.toCanvas(node, {
      backgroundColor: "#fff",
      pixelRatio: 1,
      width: widthPx,
      skipFonts: true,
    } as any);
    const image = canvas.getContext("2d")!.getImageData(0, 0, canvas.width, canvas.height);
    return { width: image.width, height: image.height, rgba: image.data };
  } finally {
    setTimeout(() => node.parentNode && document.body.removeChild(node), CLEANUP_MS);
  }
}

async function qrDataUrl(text: string): Promise<string> {
  try {
    return await QRCode.toDataURL(text, { width: 200, height: 200 } as any);
  } catch (error) {
    console.error("Error generating QR code:", error);
    return "";
  }
}

function barcodeDataUrl(value: string): string {
  try {
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, value, { format: "CODE128", height: 40, displayValue: false, textAlign: "center", fontSize: 10 });
    return canvas.toDataURL("image/png");
  } catch (error) {
    console.error("Error generating barcode:", error);
    return "";
  }
}

export const webRasterizer: Rasterizer = { toPixels, qrDataUrl, barcodeDataUrl };
